// Molecules — feature components built from atoms in components.tsx.

import React, { useRef, useState, type ReactNode } from "react";
import {
  BrandMark,
  CtxBar,
  Empty,
  Icon,
  LivePill,
  Panel,
  PillSelect,
  StatusBadge,
  Tabs,
  Toggle,
  type PillOption,
} from "./components";
import { fmtAgo, fmtClock, fmtIn } from "./utils";
// Frontend version is read from package.json (Vite/TS resolveJsonModule) so the
// top-bar pill self-updates instead of drifting from a hardcoded literal.
import pkg from "../package.json";
import type {
  AccountStatus,
  AccountView,
  ActivityEntry,
  AgentKind,
  AgentRole,
  AgentView,
  AuthedUser,
  BackendConfig,
  BlockedView,
  ChatMessage,
  PrerequisiteView,
  FromRole,
  Mode,
  RepoStatus,
  SlotGitStatus,
  SlotView,
  StateResponse,
} from "./types";

// ─── git-status badge (per-slot drift indicator) ─────────────────────────

const DIRTY_RED_THRESHOLD_MS = 60 * 60 * 1000; // dirty for >60 min → red
const NOT_CLEAN_RED_THRESHOLD_MS = 10 * 60 * 1000; // not-clean for >10 min → red
const STALE_REPORT_THRESHOLD_MS = 10 * 60 * 1000; // snapshot older than 10 min → stale (gray)

type GitTone = "green" | "yellow" | "red" | "gray";

interface GitBadgeSummary {
  tone: GitTone;
  label: string;
  title: string;
  problems: number;
}

/** A repo not on its integration branch — and not a legit tab/<op>/<N> slot
 * branch — is the silent spawn-blocker (FM5/FM7): e.g. left on `staging` or a
 * `_conflict_analysis_tmp` / `temp-conflict-test` scratch branch by a killed
 * conflict-resolver. Otherwise-clean, so it was invisible (classed green) until
 * it jammed every spawn onto the slot (incident 2026-06-10). */
export function isOffExpectedBranch(r: RepoStatus): boolean {
  return !!r.branch && !!r.integration_branch && r.branch !== r.integration_branch && !r.branch.startsWith("tab/");
}

function classifyRepoTone(r: RepoStatus, now: number): GitTone {
  if (r.state === "ahead" || r.state === "diverged" || r.state === "detached" || r.state === "fetch-fail") return "red";
  if (isOffExpectedBranch(r)) return "yellow"; // wrong-branch worktree = warning (silent spawn-blocker)
  if (r.state === "dirty") {
    if (r.dirty_oldest_mtime) {
      const dirtySince = new Date(r.dirty_oldest_mtime).getTime();
      if (now - dirtySince > DIRTY_RED_THRESHOLD_MS) return "red";
    }
    return "yellow";
  }
  if (r.state === "behind") {
    if (r.not_clean_since) {
      const since = new Date(r.not_clean_since).getTime();
      if (now - since > NOT_CLEAN_RED_THRESHOLD_MS) return "red";
    }
    return "yellow";
  }
  return "green";
}

function summarizeGitStatus(git: SlotGitStatus | null): GitBadgeSummary {
  if (!git)
    return { tone: "gray", label: "git: no data", title: "No git-status report from this slot yet.", problems: 0 };
  const now = Date.now();
  const reportedAt = new Date(git.reported_at).getTime();
  if (now - reportedAt > STALE_REPORT_THRESHOLD_MS) {
    const mins = Math.round((now - reportedAt) / 60000);
    return {
      tone: "gray",
      label: `git: stale (${mins}m)`,
      title: `Last report ${mins} min ago — reporter cron may be down on host ${git.host ?? "?"}.`,
      problems: 0,
    };
  }
  const perRepo = git.repos.map((r) => ({ r, tone: classifyRepoTone(r, now) }));
  const reds = perRepo.filter((x) => x.tone === "red").length;
  const yellows = perRepo.filter((x) => x.tone === "yellow").length;
  const tone: GitTone = reds > 0 ? "red" : yellows > 0 ? "yellow" : "green";
  const parts: string[] = [];
  if (reds > 0) parts.push(`${reds} red`);
  if (yellows > 0) parts.push(`${yellows} warn`);
  if (parts.length === 0) parts.push("clean");
  const label = `git: ${parts.join(" · ")}`;
  const titleLines = [
    `Host: ${git.host ?? "?"}`,
    `Reported: ${new Date(git.reported_at).toLocaleTimeString()}`,
    "",
    ...perRepo.map(({ r, tone: t }) => {
      const dot = t === "red" ? "●" : t === "yellow" ? "◐" : "○";
      const extras: string[] = [];
      if (isOffExpectedBranch(r)) extras.push(`on '${r.branch}' ≠ ${r.integration_branch}`);
      if (r.dirty_files > 0) extras.push(`${r.dirty_files} dirty`);
      if (r.ahead > 0) extras.push(`+${r.ahead}`);
      if (r.behind > 0) extras.push(`-${r.behind}`);
      return `${dot} ${r.name} [${r.state}]${extras.length ? " " + extras.join(" ") : ""}`;
    }),
  ];
  return { tone, label, title: titleLines.join("\n"), problems: reds + yellows };
}

function gitToneColor(tone: GitTone): string {
  switch (tone) {
    case "red":
      return "var(--status-blocked, #d4564a)";
    case "yellow":
      return "var(--status-stale, #d8a534)";
    case "green":
      return "var(--status-ok, #4ea672)";
    default:
      return "var(--fg-2, #888)";
  }
}

export function GitStatusBadge({ git }: { git: SlotGitStatus | null }) {
  const summary = summarizeGitStatus(git);
  return (
    <span
      className="git-status-badge"
      title={summary.title}
      style={{
        fontSize: 10.5,
        padding: "2px 6px",
        borderRadius: 4,
        background: gitToneColor(summary.tone),
        color: "#fff",
        whiteSpace: "nowrap",
        cursor: "help",
      }}
    >
      {summary.label}
    </span>
  );
}

export function GitStatusDetails({ git }: { git: SlotGitStatus | null }) {
  if (!git || git.repos.length === 0) return null;
  const now = Date.now();
  const reportedAt = new Date(git.reported_at).getTime();
  const stale = now - reportedAt > STALE_REPORT_THRESHOLD_MS;
  const perRepo = git.repos.map((r) => ({ r, tone: classifyRepoTone(r, now) }));
  const interesting = perRepo.filter((x) => x.tone !== "green");
  if (interesting.length === 0) return null;
  return (
    <details style={{ marginTop: 4, fontSize: 11.5 }}>
      <summary style={{ cursor: "pointer", color: "var(--fg-1)" }}>
        {interesting.length} repo{interesting.length === 1 ? "" : "s"} need attention
        {stale && <span style={{ marginLeft: 6, color: gitToneColor("gray") }}>· stale</span>}
        <span style={{ marginLeft: 6, color: "var(--fg-2)" }}>· {git.host ?? "?"}</span>
      </summary>
      <ul style={{ margin: "4px 0 0 0", padding: "0 0 0 14px", lineHeight: 1.5 }}>
        {interesting.map(({ r, tone }) => {
          const extras: string[] = [];
          if (r.dirty_files > 0) extras.push(`${r.dirty_files} file${r.dirty_files === 1 ? "" : "s"} dirty`);
          if (r.ahead > 0) extras.push(`ahead ${r.ahead}`);
          if (r.behind > 0) extras.push(`behind ${r.behind}`);
          let age = "";
          if (r.state === "dirty" && r.dirty_oldest_mtime) {
            const mins = Math.round((now - new Date(r.dirty_oldest_mtime).getTime()) / 60000);
            age = `${mins}m`;
          } else if (r.not_clean_since) {
            const mins = Math.round((now - new Date(r.not_clean_since).getTime()) / 60000);
            age = `${mins}m`;
          }
          const offBranch = isOffExpectedBranch(r);
          return (
            <li key={r.name} style={{ color: gitToneColor(tone) }}>
              <span style={{ fontWeight: 600 }}>{r.name}</span>
              <span style={{ color: "var(--fg-1)" }}> · {r.state}</span>
              {offBranch && (
                <span style={{ color: gitToneColor("red") }}>
                  {" "}
                  · on <code>{r.branch}</code> <span style={{ color: "var(--fg-2)" }}>(≠ {r.integration_branch})</span>
                </span>
              )}
              {extras.length > 0 && <span style={{ color: "var(--fg-2)" }}> · {extras.join(", ")}</span>}
              {age && <span style={{ color: "var(--fg-2)" }}> · {age}</span>}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

// ─── derived: fleet health summary ───────────────────────────────────────

export type HealthLevel = "ok" | "warn" | "crit";

export interface HealthSummary {
  level: HealthLevel;
  counts: Record<SlotView["status"], number>;
  pendingBlocked: number;
  highCtx: number;
  thrashing: number;
  total: number;
  avgCtxByStatus: Partial<Record<SlotView["status"], number>>;
}

export function summarise(state: StateResponse): HealthSummary {
  const slots = state.slots || [];
  const counts: Record<SlotView["status"], number> = {
    working: 0,
    blocked: 0,
    stale: 0,
    idle: 0,
    paused: 0,
    killed: 0,
  };
  const sumCtx: Partial<Record<SlotView["status"], number>> = {};
  const nCtx: Partial<Record<SlotView["status"], number>> = {};
  let highCtx = 0;
  let thrashing = 0;
  slots.forEach((s) => {
    counts[s.status] = (counts[s.status] || 0) + 1;
    if (s.context_used_pct >= 80) highCtx++;
    if (s.context_pressure === "thrashing") thrashing++;
    sumCtx[s.status] = (sumCtx[s.status] || 0) + s.context_used_pct;
    nCtx[s.status] = (nCtx[s.status] || 0) + 1;
  });
  const pendingBlocked = (state.blocked_queue || []).filter((b) => !b.answered_at).length;
  let level: HealthLevel = "ok";
  if (counts.stale > 0 || pendingBlocked > 0 || highCtx > 0 || thrashing > 0) level = "warn";
  if (counts.stale >= 2 || pendingBlocked >= 3 || thrashing >= 1) level = "crit";
  const avgCtxByStatus: Partial<Record<SlotView["status"], number>> = {};
  for (const k of Object.keys(sumCtx) as SlotView["status"][]) {
    const n = nCtx[k] || 1;
    avgCtxByStatus[k] = Math.round((sumCtx[k] || 0) / n);
  }
  return { counts, highCtx, pendingBlocked, thrashing, level, total: slots.length, avgCtxByStatus };
}

// ─── popover helper ──────────────────────────────────────────────────────

function usePopover() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", click);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("mousedown", click);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);
  return { open, setOpen, ref };
}

// ─── settings menu (gear popover) ────────────────────────────────────────

export type Palette = "status" | "cool" | "warm" | "mono";

export interface Tweaks {
  slotLayout: "table" | "cards";
  showActivityRail: boolean;
  showPrerequisitesRail: boolean;
  compactRows: boolean;
  accent: string;
  palette: Palette;
}

export const TWEAK_DEFAULTS: Tweaks = {
  slotLayout: "table",
  showActivityRail: true,
  showPrerequisitesRail: true,
  compactRows: false,
  accent: "#7c83ff",
  palette: "status",
};

interface SettingsMenuProps {
  t: Tweaks;
  setTweak: <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => void;
}

export function SettingsMenu({ t, setTweak }: SettingsMenuProps) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="anchor" ref={ref}>
      <button
        className={`menu-btn ${open ? "active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        title="View settings"
        aria-label="View settings"
      >
        <Icon.Gear />
      </button>
      {open && (
        <div className="menu-pop" role="menu">
          <div className="m-section">
            <div className="m-head">Layout</div>
            <div className="m-row">
              <span className="lbl">Slot view</span>
              <div className="segmented">
                <button
                  className={t.slotLayout === "table" ? "active" : ""}
                  onClick={() => setTweak("slotLayout", "table")}
                >
                  Table
                </button>
                <button
                  className={t.slotLayout === "cards" ? "active" : ""}
                  onClick={() => setTweak("slotLayout", "cards")}
                >
                  Cards
                </button>
              </div>
            </div>
            <div className="m-row">
              <span className="lbl">Compact rows</span>
              <Toggle on={t.compactRows} onChange={(v) => setTweak("compactRows", v)} />
            </div>
          </div>

          <div className="m-section">
            <div className="m-head">Right rail</div>
            <div className="m-row">
              <span className="lbl">Activity feed</span>
              <Toggle on={t.showActivityRail} onChange={(v) => setTweak("showActivityRail", v)} />
            </div>
            <div className="m-row">
              <span className="lbl">Prerequisites</span>
              <Toggle on={t.showPrerequisitesRail} onChange={(v) => setTweak("showPrerequisitesRail", v)} />
            </div>
          </div>

          <div className="m-section">
            <div className="m-head">Appearance</div>
            <div className="m-row" style={{ alignItems: "flex-start" }}>
              <span className="lbl" style={{ paddingTop: 3 }}>
                Accent
              </span>
              <div className="swatch-row">
                {["#7c83ff", "#5db4ff", "#5fd3a4", "#e3a248"].map((c) => (
                  <button
                    key={c}
                    className={`swatch ${t.accent === c ? "active" : ""}`}
                    style={{ background: c }}
                    aria-label={`Accent ${c}`}
                    onClick={() => setTweak("accent", c)}
                  />
                ))}
              </div>
            </div>
            <div className="m-row" style={{ alignItems: "flex-start" }}>
              <span className="lbl" style={{ paddingTop: 3 }}>
                Palette
              </span>
              <div className="segmented" style={{ flexWrap: "wrap" }}>
                {(["status", "cool", "warm", "mono"] as const).map((p) => (
                  <button
                    key={p}
                    className={t.palette === p ? "active" : ""}
                    onClick={() => setTweak("palette", p)}
                    title={
                      p === "status"
                        ? "Default — colors match status meaning (red=alert, green=working, etc.)"
                        : p === "cool"
                          ? "Indigo/cyan/teal/violet — red kept for Blocked"
                          : p === "warm"
                            ? "Gold/orange/coral/amber — red kept for Blocked"
                            : "Almost no color — only Blocked and Agents tinted"
                    }
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="m-section" style={{ paddingBottom: 8 }}>
            <div className="m-row" style={{ color: "var(--fg-2)", fontSize: 11.5, cursor: "default" }}>
              <span className="lbl">Polling cadence</span>
              <span className="mono" style={{ color: "var(--fg-1)" }}>
                60s
              </span>
            </div>
            <div className="m-row" style={{ color: "var(--fg-2)", fontSize: 11.5, cursor: "default" }}>
              <span className="lbl">Backend list</span>
              <span className="hint mono">backends.json</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── user chip ───────────────────────────────────────────────────────────

interface UserChipProps {
  user: AuthedUser;
  sessionStartedShort?: string;
  onSignOut: () => void;
}

export function UserChip({ user, sessionStartedShort, onSignOut }: UserChipProps) {
  const { open, setOpen, ref } = usePopover();
  const initials = (user.name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  return (
    <div className="anchor" ref={ref}>
      <button className="user-chip" onClick={() => setOpen((o) => !o)} aria-label="Account menu">
        <span className="avatar">{initials}</span>
        <span className="name hide-sm">{user.name}</span>
        <Icon.Chev style={{ color: "var(--fg-2)", transform: open ? "rotate(90deg)" : "" }} />
      </button>
      {open && (
        <div className="menu-pop" style={{ minWidth: 240 }} role="menu">
          <div className="m-section">
            <div className="m-row" style={{ cursor: "default" }}>
              <span className="avatar" style={{ width: 28, height: 28, fontSize: 12 }}>
                {initials}
              </span>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                <span style={{ fontWeight: 600, color: "var(--fg-0)" }}>{user.name}</span>
                <span className="mono" style={{ fontSize: 11, color: "var(--fg-2)" }}>
                  {user.role || "operator"}
                </span>
              </div>
            </div>
          </div>
          <div className="m-section">
            <div className="m-row" style={{ cursor: "default" }}>
              <span className="lbl" style={{ color: "var(--fg-2)" }}>
                Session started
              </span>
              <span className="mono" style={{ fontSize: 11, color: "var(--fg-1)" }}>
                {sessionStartedShort || "—"}
              </span>
            </div>
            <div className="m-row" style={{ cursor: "default" }}>
              <span className="lbl" style={{ color: "var(--fg-2)" }}>
                Machine
              </span>
              <span className="mono" style={{ fontSize: 11, color: "var(--fg-1)" }}>
                {user.machine || "—"}
              </span>
            </div>
          </div>
          <div className="m-section" style={{ padding: 6 }}>
            <button
              className="btn btn-sm"
              style={{ width: "100%", justifyContent: "flex-start", background: "transparent", border: "none" }}
              onClick={onSignOut}
            >
              <Icon.SignOut /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── top bar ─────────────────────────────────────────────────────────────

interface TopBarProps {
  backends: BackendConfig[];
  backendId: string;
  onBackend: (id: string) => void;
  state: StateResponse | null;
  onSnapshot: () => void;
  connected: boolean;
  mode: Mode | null;
  t: Tweaks;
  setTweak: <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => void;
  user: AuthedUser;
  sessionStartedShort?: string;
  onSignOut: () => void;
}

export function TopBar({
  backends,
  backendId,
  onBackend,
  state,
  onSnapshot,
  connected,
  mode,
  t,
  setTweak,
  user,
  sessionStartedShort,
  onSignOut,
}: TopBarProps) {
  const lastUpdated = state ? fmtAgo(state.last_updated) : "—";
  const pillOpts: PillOption[] = backends.map((b) => ({
    id: b.id,
    label: b.label,
    url: b.url,
    status: b.status,
    last_pinged_at: b.last_pinged_at,
  }));
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <span className="brand">
          <BrandMark />
          <span>Orchestrator</span>
          <small>
            api {state?.version ?? "—"} · ui {pkg.version}
          </small>
        </span>

        {mode && <ModeBadge mode={mode} />}

        <span className="topbar-spacer" />

        <div className="topbar-meta">
          <LivePill connected={connected} lastUpdated={lastUpdated} serverStarted={state?.server_started} />
          <button
            className="btn btn-sm hide-sm"
            onClick={onSnapshot}
            title="Snapshot — writes state.json + commits to git"
          >
            <Icon.Camera /> Snapshot
          </button>
          <PillSelect
            value={backendId}
            options={pillOpts}
            onChange={onBackend}
            formatLabel={(b) => (
              <>
                <span className={`status-dot ${b.status === "ok" ? "working" : "stale"}`} />
                <span className="label label-mono">{b.id}</span>
                <span className="url hide-sm">{b.url.replace(/^https?:\/\//, "")}</span>
              </>
            )}
          />
          <SettingsMenu t={t} setTweak={setTweak} />
          <UserChip user={user} sessionStartedShort={sessionStartedShort} onSignOut={onSignOut} />
        </div>
      </div>
    </header>
  );
}

function ModeBadge({ mode }: { mode: Mode }) {
  const label = mode === "mock" ? "MOCK" : "LIVE";
  const cls = mode === "mock" ? "mode-mock" : "mode-live";
  const title =
    mode === "mock"
      ? "Mock mode — fake data; admin endpoints available; switch via ORCHESTRATOR_MODE=live"
      : "Live mode — real data; admin endpoints disabled";
  return (
    <span className={`mode-badge ${cls}`} title={title}>
      {label}
    </span>
  );
}

// ─── health strip ────────────────────────────────────────────────────────

export function HealthStrip({ summary }: { summary: HealthSummary }) {
  const { level, counts, pendingBlocked, highCtx, thrashing, total } = summary;
  const titles = {
    ok: "All systems normal",
    warn: "Needs attention",
    crit: "Multiple issues — eyes on this",
  };
  const icon = level === "ok" ? <Icon.Heart /> : <Icon.Warn />;

  const subParts: ReactNode[] = [];
  if (counts.working)
    subParts.push(
      <span key="w">
        <b>{counts.working}</b> working
      </span>,
    );
  if (pendingBlocked)
    subParts.push(
      <span key="b" style={{ color: "var(--status-blocked)" }}>
        <b>{pendingBlocked}</b> blocked question{pendingBlocked > 1 ? "s" : ""}
      </span>,
    );
  if (counts.stale)
    subParts.push(
      <span key="s" style={{ color: "var(--status-stale)" }}>
        <b>{counts.stale}</b> stale slot{counts.stale > 1 ? "s" : ""}
      </span>,
    );
  if (thrashing)
    subParts.push(
      <span key="t" style={{ color: "var(--status-stale)" }}>
        <b>{thrashing}</b> thrashing
      </span>,
    );
  if (highCtx)
    subParts.push(
      <span key="h" style={{ color: "var(--status-stale)" }}>
        <b>{highCtx}</b> at high context
      </span>,
    );
  if (counts.idle)
    subParts.push(
      <span key="i">
        <b>{counts.idle}</b> idle
      </span>,
    );
  if (counts.paused)
    subParts.push(
      <span key="p">
        <b>{counts.paused}</b> paused
      </span>,
    );
  if (subParts.length === 0)
    subParts.push(
      <span key="n">
        {total} slot{total === 1 ? "" : "s"}, nothing wrong
      </span>,
    );

  return (
    <div className={`health ${level}`}>
      <span className="health-icon">{icon}</span>
      <div className="copy">
        <div className="title">{titles[level]}</div>
        <div className="sub">{subParts}</div>
      </div>
      <div className="actions">
        {pendingBlocked > 0 && (
          <a href="#blocked">
            <button className="btn btn-sm">
              Answer {pendingBlocked} question{pendingBlocked > 1 ? "s" : ""}
            </button>
          </a>
        )}
        {counts.stale > 0 && (
          <a href="#fleet">
            <button className="btn btn-sm btn-danger">Review stale slots</button>
          </a>
        )}
      </div>
    </div>
  );
}

// ─── KPI row ─────────────────────────────────────────────────────────────

interface KpiRowProps {
  summary: HealthSummary;
  backlog: StateResponse["backlog_summary"];
}

export function KpiRow({ summary, backlog }: KpiRowProps) {
  const { counts, total } = summary;
  return (
    <div className="kpi-row">
      <div className="kpi">
        <div className="k-label">Slots</div>
        <div className="k-value">{total}</div>
        <div className="k-sub">
          {summary.pendingBlocked} blocked · {summary.highCtx} high ctx · {summary.thrashing} thrashing
        </div>
      </div>
      <div className="kpi k-working">
        <div className="k-label">
          <span className="k-dot working" /> Working
        </div>
        <div className="k-value">{counts.working || 0}</div>
        <div className="k-sub">avg ctx {summary.avgCtxByStatus.working ?? "—"}%</div>
      </div>
      <div className="kpi k-blocked">
        <div className="k-label">
          <span className="k-dot blocked" /> Blocked
        </div>
        <div className="k-value">{counts.blocked || 0}</div>
        <div className="k-sub">awaiting answer</div>
      </div>
      <div className="kpi k-stale">
        <div className="k-label">
          <span className="k-dot stale" /> Stale
        </div>
        <div className="k-value">{counts.stale || 0}</div>
        <div className="k-sub">silent &gt; 20m</div>
      </div>
      <div className="kpi">
        <div className="k-label">
          <span className="k-dot idle" /> Idle
        </div>
        <div className="k-value">{counts.idle || 0}</div>
        <div className="k-sub">awaiting task</div>
      </div>
      <div className="kpi">
        <div className="k-label">Backlog</div>
        <div className="k-value">{backlog.queued}</div>
        <div className="k-sub">
          {backlog.dispatched} dispatched · {backlog.done} done
        </div>
      </div>
    </div>
  );
}

// ─── slot table (hybrid expandable) ──────────────────────────────────────

export type SlotAction = "pause" | "resume" | "message" | "reassign" | "delete" | "log" | "loop-interval";

interface SlotTableProps {
  slots: SlotView[];
  onAct: (action: SlotAction, slot: SlotView) => void;
  expandedId: number | null;
  setExpandedId: (id: number | null) => void;
}

export function SlotTable({ slots, onAct, expandedId, setExpandedId }: SlotTableProps) {
  return (
    <table className="slot-table">
      <thead>
        <tr>
          <th style={{ width: 60 }}>Slot</th>
          <th style={{ width: 100 }}>Status</th>
          <th style={{ width: 75 }}>Source</th>
          <th style={{ width: 120 }}>Git</th>
          <th style={{ width: 90 }}>Task</th>
          <th style={{ width: 130 }}>Plan</th>
          <th>Last message</th>
          <th style={{ width: 160 }}>Context</th>
          <th style={{ width: 70 }}>Ping</th>
          <th style={{ width: 110 }}>Model · Op</th>
          <th style={{ width: 110, textAlign: "right" }}></th>
        </tr>
      </thead>
      <tbody>
        {slots.map((s) => {
          const open = expandedId === s.slot_id;
          // Paused slots don't have a polling worker, so last_ping is meaningless
          // (it's from whoever booted there last). Show "—" instead of a stale age.
          const ago = s.status === "paused" ? "—" : fmtAgo(s.last_ping);
          return (
            <React.Fragment key={s.slot_id}>
              <tr className={`row ${open ? "expanded" : ""}`} onClick={() => setExpandedId(open ? null : s.slot_id)}>
                <td>
                  <span className="slot-id">
                    <span className="chev">
                      <Icon.Chev />
                    </span>
                    #{s.slot_id}
                  </span>
                </td>
                <td>
                  <StatusBadge status={s.status} />
                </td>
                <td>
                  {s.current_task ? (
                    <span
                      style={{
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 3,
                        background: "rgba(99,102,241,0.15)",
                        color: "var(--fg-2)",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                      }}
                    >
                      plan
                    </span>
                  ) : (
                    <span style={{ color: "var(--fg-3)" }}>—</span>
                  )}
                </td>
                <td>
                  <GitStatusBadge git={s.git_status} />
                </td>
                <td>
                  {s.current_task ? (
                    <span className="task-id mono">{s.current_task}</span>
                  ) : (
                    <span className="task-id empty mono">—</span>
                  )}
                </td>
                <td
                  style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={s.plan_ref || ""}
                >
                  {s.plan_ref ? (
                    <span className="task-id mono">
                      {s.plan_ref.replace(/^plans\/active\//, "").replace(/\.md$/, "")}
                    </span>
                  ) : (
                    <span className="task-id empty mono">—</span>
                  )}
                </td>
                <td
                  style={{
                    color: "var(--fg-1)",
                    maxWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={s.last_msg || ""}
                >
                  {s.last_msg || ""}
                </td>
                <td>
                  <CtxBar
                    pct={s.context_used_pct}
                    pressure={s.context_pressure}
                    compactionsLastHour={s.compactions_last_hour}
                  />
                </td>
                <td>
                  <span className={`ago-cell ${s.status === "stale" ? "stale" : ""}`}>{ago}</span>
                </td>
                <td className="model-cell">
                  <ModelBadge model={s.model} effort={s.effort} thinking={s.thinking} />{" "}
                  <span style={{ color: "var(--fg-3)" }}>{s.operator || ""}</span>
                  {s.tmux_session && s.tmux_alive && (
                    <span
                      className="mono"
                      title={`tmux session alive. Attach: tmux attach -t ${s.tmux_session}`}
                      style={{
                        marginLeft: 6,
                        fontSize: 10,
                        padding: "1px 5px",
                        borderRadius: 3,
                        background: "var(--bg-3)",
                        color: "var(--fg-2)",
                      }}
                    >
                      tmux
                    </span>
                  )}
                  {s.worker_alive && !s.tmux_alive && (
                    <span
                      className="mono"
                      title="Worker process is heartbeating but its tmux session is gone (likely killed by prune wave). `tmux attach` will not work."
                      style={{
                        marginLeft: 6,
                        fontSize: 10,
                        padding: "1px 5px",
                        borderRadius: 3,
                        background: "var(--warn-bg, #4a3a1a)",
                        color: "var(--warn-fg, #ffd479)",
                      }}
                    >
                      bash-only
                    </span>
                  )}
                </td>
                <td>
                  <div className="row-actions">
                    {s.status === "paused" ? (
                      <button
                        className="btn btn-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAct("resume", s);
                        }}
                        title="Resume"
                      >
                        <Icon.Play />
                      </button>
                    ) : (
                      <button
                        className="btn btn-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAct("pause", s);
                        }}
                        title="Pause"
                      >
                        <Icon.Pause />
                      </button>
                    )}
                    <button
                      className="btn btn-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAct("message", s);
                      }}
                      title="Send message"
                    >
                      <Icon.Send />
                    </button>
                    <button
                      className="btn btn-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAct("loop-interval", s);
                      }}
                      title="Set /loop cadence (live — re-issues the loop, no respawn)"
                    >
                      <Icon.Bolt />
                    </button>
                    <button
                      className="btn btn-xs btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAct("reassign", s);
                      }}
                      disabled={!s.current_task}
                      title="Reassign task to queue"
                    >
                      <Icon.Refresh />
                    </button>
                  </div>
                </td>
              </tr>
              {open && (
                <tr className="detail-row">
                  <td colSpan={10}>
                    <div className="detail-card">
                      <div>
                        <div className="field">
                          <span className="label">Last message</span>
                          <div className="msg">{s.last_msg || "—"}</div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          <div className="field">
                            <span className="label">Worktree</span>
                            <span className="value">{s.worktree || "—"}</span>
                          </div>
                          <div className="field">
                            <span className="label">Branch</span>
                            <span className="value">{s.branch || "—"}</span>
                          </div>
                          <div className="field">
                            <span className="label">Assigned</span>
                            <span className="value">
                              {s.assigned_at ? fmtClock(s.assigned_at) + " UTC · " + fmtAgo(s.assigned_at) : "—"}
                            </span>
                          </div>
                          <div className="field">
                            <span className="label">Last ping</span>
                            <span className="value">
                              {fmtClock(s.last_ping)} UTC · {fmtAgo(s.last_ping)}
                            </span>
                          </div>
                          <div className="field">
                            <span className="label">Compactions</span>
                            <span className="value">
                              {s.compactions_total} total · {s.compactions_last_hour} in last hr · last{" "}
                              {fmtAgo(s.last_compacted_at)}
                            </span>
                          </div>
                          <div className="field">
                            <span className="label">Context</span>
                            <span className="value">
                              {s.context_used_pct}% ({s.context_pressure})
                            </span>
                          </div>
                          {s.tmux_session && (
                            <div className="field" style={{ gridColumn: "1 / -1" }}>
                              <span className="label">tmux session</span>
                              <span className="value" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <span className="mono">{s.tmux_session}</span>
                                <button
                                  className="btn btn-xs"
                                  onClick={() => {
                                    void navigator.clipboard?.writeText(`tmux attach -t ${s.tmux_session}`);
                                  }}
                                  title="Copy local-attach command"
                                >
                                  Copy attach
                                </button>
                                <button
                                  className="btn btn-xs"
                                  onClick={() => {
                                    const host =
                                      window.location.hostname === "localhost"
                                        ? "<your-host>"
                                        : window.location.hostname;
                                    void navigator.clipboard?.writeText(
                                      `ssh ${host} -t tmux attach -t ${s.tmux_session}`,
                                    );
                                  }}
                                  title="Copy SSH+attach command (for mobile / remote use)"
                                >
                                  Copy ssh+attach
                                </button>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="danger-zone">
                          <div className="label">Operator actions</div>
                          <div className="detail-actions">
                            <button className="btn btn-sm" onClick={() => onAct("message", s)}>
                              <Icon.Send /> Send message…
                            </button>
                            <button
                              className="btn btn-sm"
                              onClick={() => onAct("log", s)}
                              title="Tail tmux capture-pane scrollback in a modal (auto-refreshes every 5s)"
                            >
                              Show log
                            </button>
                            {s.status === "paused" ? (
                              <button className="btn btn-sm" onClick={() => onAct("resume", s)}>
                                <Icon.Play /> Resume
                              </button>
                            ) : (
                              <button className="btn btn-sm" onClick={() => onAct("pause", s)}>
                                <Icon.Pause /> Pause
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => onAct("reassign", s)}
                              disabled={!s.current_task}
                            >
                              <Icon.Refresh /> Reassign task
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => onAct("delete", s)}
                              disabled={!!s.current_task}
                              title={
                                s.current_task
                                  ? "Reassign the task first, then delete the slot"
                                  : "Remove this slot row + kill its tmux session"
                              }
                            >
                              Delete slot
                            </button>
                          </div>
                          <div style={{ color: "var(--fg-2)", fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
                            Reassign returns <b style={{ color: "var(--fg-0)" }}>{s.current_task || "the task"}</b> to
                            the queue and frees this slot. Delete removes the slot row entirely and kills the tmux
                            session if attached. Both confirm before running.
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── slot cards (alternative layout) ─────────────────────────────────────

export function SlotCards({ slots, onAct }: { slots: SlotView[]; onAct: SlotTableProps["onAct"] }) {
  return (
    <div className="slot-cards">
      {slots.map((s) => (
        <div key={s.slot_id} className={`slot-card ${s.status}`}>
          <div className="row-1">
            <span className="id mono">#{s.slot_id}</span>
            <StatusBadge status={s.status} />
            <GitStatusBadge git={s.git_status} />
            <span className="spacer" />
            <span className="model-cell">
              <ModelBadge model={s.model} effort={s.effort} thinking={s.thinking} />
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--fg-1)", lineHeight: 1.45, minHeight: 34 }}>
            {s.current_task && (
              <span className="task-id mono" style={{ marginRight: 6 }}>
                {s.current_task}
              </span>
            )}
            {s.last_msg || ""}
            <GitStatusDetails git={s.git_status} />
          </div>
          <CtxBar
            pct={s.context_used_pct}
            pressure={s.context_pressure}
            compactionsLastHour={s.compactions_last_hour}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="ago-cell" style={{ fontSize: 11.5 }}>
              {fmtAgo(s.last_ping)}
            </span>
            <span className="spacer" style={{ flex: 1 }} />
            <div className="row-actions">
              {s.status === "paused" ? (
                <button className="btn btn-xs" onClick={() => onAct("resume", s)}>
                  <Icon.Play />
                </button>
              ) : (
                <button className="btn btn-xs" onClick={() => onAct("pause", s)}>
                  <Icon.Pause />
                </button>
              )}
              <button className="btn btn-xs" onClick={() => onAct("message", s)}>
                <Icon.Send />
              </button>
              <button className="btn btn-xs btn-danger" disabled={!s.current_task} onClick={() => onAct("reassign", s)}>
                <Icon.Refresh />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── blocked questions ───────────────────────────────────────────────────

interface BlockedPanelProps {
  queue: BlockedView[];
  onAnswer: (q: BlockedView, answer: string, fromRole: FromRole) => void;
}

export function BlockedPanel({ queue, onAnswer }: BlockedPanelProps) {
  const pending = queue.filter((q) => q.answered_at === null);
  // Telemetry chip (2026-06-12): main-vs-operator answered split + median
  // time-to-answer, computed from the full queue the panel already receives.
  // The main agent is the standing FIRST responder (MainAgentKeeper) — this
  // is its SLA at a glance; GET /api/blocked/stats serves the same rollup to
  // non-UI consumers.
  const answered = queue.filter((q) => q.answered_at !== null);
  const byMain = answered.filter((q) => (q.answered_by ?? "").trim() === "main").length;
  const ttas = answered
    .map((q) => (q.answered_at ? Date.parse(q.answered_at) - Date.parse(q.created_at) : NaN))
    .filter((ms) => Number.isFinite(ms) && ms >= 0)
    .sort((a, b) => a - b);
  const medianMin = ttas.length > 0 ? Math.round(ttas[Math.floor((ttas.length - 1) / 2)] / 60000) : null;
  const statsLabel =
    answered.length > 0
      ? `${byMain}/${answered.length} by main${medianMin !== null ? ` · median ${medianMin}m` : ""}`
      : null;
  return (
    <Panel
      title="Blocked questions"
      count={pending.length}
      right={
        <>
          {statsLabel && <span className="livepill">{statsLabel}</span>}
          {pending.length > 0 && (
            <span className="livepill">
              <span className="dot" style={{ background: "var(--status-blocked)" }} />
              awaiting
            </span>
          )}
        </>
      }
      flush
      tone="blocked"
    >
      {pending.length === 0 ? (
        <Empty
          icon={<Icon.Check />}
          title="No questions waiting"
          sub="When a worker or review agent hits ambiguity, it'll show up here."
        />
      ) : (
        <div className="blocked-list">
          {pending.map((q) => (
            <BlockedCard key={q.blocked_id} q={q} onAnswer={onAnswer} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function BlockedCard({ q, onAnswer }: { q: BlockedView; onAnswer: BlockedPanelProps["onAnswer"] }) {
  const [text, setText] = useState("");
  const [fromRole, setFromRole] = useState<FromRole>("main");
  const recIdx = q.recommendation ? q.recommendation.charCodeAt(0) - 65 : -1;
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = (answer: string) => {
    if (!answer.trim()) return;
    onAnswer(q, answer, fromRole);
    setText("");
  };

  // authority="operator" → amber border (human-only decision);
  // authority="main_agent" → default blue border (main agent may answer).
  const authorityClass = (q.authority ?? "main_agent") === "operator" ? "authority-operator" : "authority-main";

  return (
    <div className={`blocked-card ${authorityClass}`}>
      <div className="head">
        <span className="slot-ref">#{q.slot_id}</span>
        <span className="task-id mono">{q.task_id}</span>
        {(q.authority ?? "main_agent") === "operator" && (
          <span className="authority-badge" title="Only an operator (Harsh/Ikenna) should answer this question">
            operator-only
          </span>
        )}
        <span className="spacer" />
        <span>{fmtAgo(q.created_at)}</span>
      </div>
      <div className="question">{q.question}</div>
      <div className="options">
        {q.options.map((opt, i) => {
          const letter = opt.match(/^([A-Z]):/)?.[1] ?? String.fromCharCode(65 + i);
          const rest = opt.replace(/^([A-Z]):\s*/, "");
          const rec = i === recIdx;
          return (
            <button key={i} className={`opt ${rec ? "recommended" : ""}`} onClick={() => submit(opt)}>
              <span className="letter">{letter}</span>
              <span style={{ flex: 1 }}>{rest}</span>
              {rec && <span className="rec-tag">worker rec</span>}
            </button>
          );
        })}
        <button
          className="opt"
          onClick={() => {
            setText("");
            inputRef.current?.focus();
          }}
        >
          <span className="letter">…</span>
          <span style={{ flex: 1 }}>Other — type a custom answer</span>
        </button>
      </div>
      <div className="answer-row">
        <select
          value={fromRole}
          onChange={(e) => setFromRole(e.target.value as FromRole)}
          style={{ width: "auto", minWidth: 88 }}
          title="Whose decision is this — main agent, review agent, or you (operator)"
        >
          <option value="main">as main</option>
          <option value="review">as review</option>
          <option value="operator">as operator</option>
        </select>
        <input
          ref={inputRef}
          type="text"
          placeholder="…or type a custom answer"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit(text);
          }}
        />
        <button className="btn btn-primary btn-sm" disabled={!text.trim()} onClick={() => submit(text)}>
          <Icon.Send /> Send
        </button>
      </div>
    </div>
  );
}

// ─── prerequisites ──────────────────────────────────────────────────────────

interface PrerequisitesPanelProps {
  prerequisites: Record<string, PrerequisiteView>;
  onToggle: (name: string, value: boolean) => void;
}

// Plan B (audit Ask 3): show the most-actionable few + collapse the rest.
const CONDITIONS_COLLAPSED_COUNT = 5;
// When expanded, cap the visible height at ~15 rows; beyond that the list
// scrolls and is drag-resizable (operator 2026-06-22). ~31rem ≈ 15 × 33px rows.
// `height` (not maxHeight) is the initial size so the native resize handle can
// grow it past the default, not just shrink it.
const CONDITIONS_MAX_VISIBLE = 15;
const CONDITIONS_EXPANDED_HEIGHT = "31rem";

/** Sort prerequisites for display: gates_queued>0 (tasks actually waiting) first,
 * then OFF prerequisites (likely the next gate to flip), then alpha for stability.
 * Returns `[name, view]` tuples so the panel maps directly. */
export function sortPrerequisitesForDisplay(
  prerequisites: Record<string, PrerequisiteView>,
): Array<[string, PrerequisiteView]> {
  return Object.entries(prerequisites).sort(([an, a], [bn, b]) => {
    const aBlocking = a.gates_queued > 0 && !a.value ? 1 : 0;
    const bBlocking = b.gates_queued > 0 && !b.value ? 1 : 0;
    if (aBlocking !== bBlocking) return bBlocking - aBlocking; // blocking gates first
    const aOff = a.value ? 1 : 0;
    const bOff = b.value ? 1 : 0;
    if (aOff !== bOff) return aOff - bOff; // OFF (0) before ON (1)
    return an.localeCompare(bn);
  });
}

export function PrerequisitesPanel({ prerequisites, onToggle }: PrerequisitesPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const sorted = sortPrerequisitesForDisplay(prerequisites);
  const onCount = sorted.filter(([, v]) => v.value).length;
  const total = sorted.length;
  const collapsible = total > CONDITIONS_COLLAPSED_COUNT;
  const shown = expanded || !collapsible ? sorted : sorted.slice(0, CONDITIONS_COLLAPSED_COUNT);
  const hiddenCount = total - shown.length;
  // Once expanded past 15 rows, cap the height + make the list scroll + drag-resize
  // (operator 2026-06-22) so a long prerequisites list never pushes the page down.
  const scrollWhenExpanded = expanded && total > CONDITIONS_MAX_VISIBLE;

  return (
    <Panel title="Prerequisites" count={`${onCount}/${total}`} flush tone="prerequisites">
      {total === 0 ? (
        <Empty
          title="No prerequisites yet"
          sub="Define gates in backlog.yaml; toggling them here unblocks downstream tasks."
        />
      ) : (
        <>
          <div
            className="cond-list"
            style={
              scrollWhenExpanded
                ? { height: CONDITIONS_EXPANDED_HEIGHT, overflow: "auto", resize: "vertical" }
                : undefined
            }
          >
            {shown.map(([name, c]) => (
              <div key={name} className="cond">
                <span className="name" title={name}>
                  {name}
                </span>
                {c.gates_queued > 0 && !c.value && (
                  <span
                    className="mono"
                    style={{ fontSize: 10.5, color: "var(--status-blocked)", marginLeft: 4 }}
                    title={`${c.gates_queued} queued task${c.gates_queued === 1 ? "" : "s"} waiting on this gate`}
                  >
                    gates {c.gates_queued}
                  </span>
                )}
                <span className="meta">{c.set_by ? `${c.set_by} · ${fmtAgo(c.set_at)}` : ""}</span>
                <Toggle on={c.value} onChange={(v) => onToggle(name, v)} />
              </div>
            ))}
          </div>
          {collapsible && (
            <button
              className="btn btn-xs btn-ghost"
              style={{ margin: "6px auto", display: "block" }}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Collapse ▴" : `Show ${hiddenCount} more ▾`}
            </button>
          )}
        </>
      )}
    </Panel>
  );
}

// ─── activity feed ───────────────────────────────────────────────────────
//
// Plan B (agent_orchestrator_dashboard_monitoring): server-driven filter tabs
// (the category's event_types are passed to the backend as `types=` CSV so the
// SQL filter runs BEFORE the limit), cursor pagination ("Load older" via
// `before_id`), and ×N row collapse + expand (the "90% repeats" fix).

export type ActivityFilter = "all" | "alerts" | "ops";

// The category type-sets are single-sourced here so the SAME list drives both
// the client predicate AND the `types=` CSV sent to the server (audit Ask 2).
export const ALERT_TYPES = [
  "slot_blocked",
  "slot_stale",
  "slot_reassigned",
  "slot_compacted",
  "agent_stale",
  "escalation_dispatch_failed",
  "autospawn_failed",
  "account_rotation_failed",
  "agentkeeper_review_failed",
  "watchdog_usage_cap_frozen",
  "failover_rerouted",
] as const;

export const OPS_TYPES = [
  "task_dispatched",
  "blocked_answered",
  "prerequisite_set",
  "session_checkpoint",
  "slot_paused",
  "slot_resumed",
  "slot_boot",
  "agent_restored",
  "message_sent",
] as const;

/** The comma-separated `types=` value the server query expects for a category
 * tab; `undefined` for "all" (no filter). Single source for client + server. */
export function categoryTypesCsv(filter: ActivityFilter): string | undefined {
  if (filter === "alerts") return ALERT_TYPES.join(",");
  if (filter === "ops") return OPS_TYPES.join(",");
  return undefined;
}

const ALERT_TYPE_SET: ReadonlySet<string> = new Set(ALERT_TYPES);
const OPS_TYPE_SET: ReadonlySet<string> = new Set(OPS_TYPES);

/** A failure-class event whose `details.error` (if present) we render inline +
 * flag red (audit Ask: surface WHY a dispatch failed without DB access). Open-ended
 * by design — any `*_failed`/`*_dead`/`*_quarantined`/`*_error` event qualifies,
 * so a new failure event type renders its reason without a code change. */
export function isFailureEvent(eventType: string): boolean {
  return /(_failed|_fail|_error|_dead|_quarantined|_stuck|_down)$/.test(eventType) || eventType === "slot_stale";
}

/** Pull a human error string out of an activity row's details_json. The server
 * persists the reason under `error` (escalation_dispatch_failed et al.); we also
 * accept `last_error`/`reason`/`detail` so the renderer is robust to the writer. */
export function extractFailureError(details: Record<string, unknown> | null | undefined): string | null {
  if (!details) return null;
  for (const key of ["error", "last_error", "reason", "detail", "message"]) {
    const v = details[key];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

export interface CollapsedActivity {
  /** Most-recent representative row of the group (drives the row render). */
  head: ActivityEntry;
  /** How many rows collapsed into this group (≥1). Renders as "×N" when >1. */
  count: number;
  /** All rows in the group, newest-first, for the expanded view. */
  rows: ActivityEntry[];
  /** Stable key for React + expand state — event_type[:slot]. */
  key: string;
}

/** Collapse adjacent same-(event_type, slot) rows into ×N groups (the "90%
 * repeating alerts" denoise). Input is newest-first; a run of identical
 * (type, slot) rows folds into one group keyed by the run, preserving order so
 * distinct bursts stay distinct. */
export function collapseActivity(entries: ActivityEntry[]): CollapsedActivity[] {
  const out: CollapsedActivity[] = [];
  for (const e of entries) {
    const last = out[out.length - 1];
    if (last && last.head.event_type === e.event_type && last.head.slot_id === e.slot_id) {
      last.count += 1;
      last.rows.push(e);
    } else {
      out.push({
        head: e,
        count: 1,
        rows: [e],
        key: `${e.event_type}:${e.slot_id ?? "-"}:${e.id ?? out.length}`,
      });
    }
  }
  return out;
}

interface ActivityFeedProps {
  activity: ActivityEntry[];
  /** Selected category tab (lifted to the parent so the server refetch with the
   * category's `types=` CSV is driven from there). Defaults to local-only if absent. */
  filter?: ActivityFilter;
  onFilterChange?: (f: ActivityFilter) => void;
  /** "Load older" — parent fetches `before_id = oldest row id` and appends. */
  onLoadOlder?: (beforeId: number) => void;
  loadingOlder?: boolean;
  /** False once the server returns a short page (no more history). */
  hasMore?: boolean;
}

export function ActivityFeed({
  activity,
  filter,
  onFilterChange,
  onLoadOlder,
  loadingOlder = false,
  hasMore = false,
}: ActivityFeedProps) {
  // Self-contained fallback when the parent doesn't lift the filter (e.g. the
  // mobile/no-slots layout): filter client-side over the polled page.
  const [localView, setLocalView] = useState<ActivityFilter>("all");
  const view = filter ?? localView;
  const setView = onFilterChange ?? setLocalView;
  const serverDriven = filter !== undefined; // parent already filtered via `types=`
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Noise toggle: when false (default), hide events not in ALERT_TYPES or OPS_TYPES on the
  // "all" tab — backend heartbeats and polling noise stay hidden until the operator opts in.
  const [showBackendNoise, setShowBackendNoise] = useState(false);
  // Datetime-range filter (default: last 2h). sinceHours=0 means no lower bound.
  const [sinceHours, setSinceHours] = useState(2);
  const q = search.trim().toLowerCase();

  const sinceMs = sinceHours > 0 ? Date.now() - sinceHours * 3600_000 : 0;

  const filtered = activity.filter((a) => {
    // Datetime lower-bound filter.
    if (sinceMs > 0 && new Date(a.ts).getTime() < sinceMs) return false;
    // Only client-filter by category when the parent is NOT server-filtering.
    if (!serverDriven) {
      if (view === "alerts" && !ALERT_TYPE_SET.has(a.event_type) && !isFailureEvent(a.event_type)) return false;
      if (view === "ops" && !OPS_TYPE_SET.has(a.event_type)) return false;
      // Noise suppression on "all" tab: hide events outside both known sets unless toggled on.
      if (
        view === "all" &&
        !showBackendNoise &&
        !ALERT_TYPE_SET.has(a.event_type) &&
        !OPS_TYPE_SET.has(a.event_type) &&
        !isFailureEvent(a.event_type)
      )
        return false;
    }
    if (q) {
      const hay = [
        a.event_type,
        a.slot_id != null ? `#${a.slot_id} slot${a.slot_id}` : "",
        a.task_id || "",
        a.details ? JSON.stringify(a.details).toLowerCase() : "",
      ]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const groups = collapseActivity(filtered);
  const oldestId = filtered.length > 0 ? filtered[filtered.length - 1]!.id : null;

  return (
    <Panel
      title="Activity"
      count={filtered.length}
      flush
      tone="activity"
      right={
        <Tabs<ActivityFilter>
          value={view}
          onChange={setView}
          options={[
            { value: "all", label: "All" },
            { value: "alerts", label: "Alerts" },
            { value: "ops", label: "Ops" },
          ]}
        />
      }
    >
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="search"
          placeholder="Filter by slot (e.g. #4), task (R-001), or event type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 180,
            fontSize: 12,
            padding: "5px 8px",
            background: "var(--bg-1)",
            color: "var(--fg-0)",
            border: "1px solid var(--line)",
            borderRadius: 4,
            fontFamily: "var(--font-mono)",
          }}
        />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: "var(--fg-2)",
            whiteSpace: "nowrap",
          }}
        >
          Last
          <select
            value={sinceHours}
            onChange={(e) => setSinceHours(Number(e.target.value))}
            style={{
              fontSize: 12,
              padding: "3px 6px",
              background: "var(--bg-1)",
              color: "var(--fg-0)",
              border: "1px solid var(--line)",
              borderRadius: 4,
            }}
          >
            <option value={1}>1h</option>
            <option value={2}>2h</option>
            <option value={6}>6h</option>
            <option value={24}>24h</option>
            <option value={0}>All time</option>
          </select>
        </label>
        {view === "all" && (
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              color: "var(--fg-2)",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            <input type="checkbox" checked={showBackendNoise} onChange={(e) => setShowBackendNoise(e.target.checked)} />
            Show backend noise
          </label>
        )}
      </div>
      {filtered.length === 0 ? (
        <Empty
          title={q || view !== "all" ? "No matching events" : "No activity to show"}
          sub={
            q || view !== "all"
              ? "Clear the filter to see all events."
              : "Events stream into here every time a worker boots, pings, finishes, or escalates."
          }
        />
      ) : (
        <div className="activity">
          {groups.map((g) => (
            <ActivityGroupRow
              key={g.key}
              group={g}
              expanded={!!expanded[g.key]}
              onToggle={() => setExpanded((m) => ({ ...m, [g.key]: !m[g.key] }))}
            />
          ))}
          {onLoadOlder && hasMore && oldestId != null && (
            <button
              className="btn btn-xs btn-ghost"
              style={{ margin: "8px auto", display: "block" }}
              disabled={loadingOlder}
              onClick={() => onLoadOlder(oldestId)}
              title="Fetch older activity from the server (cursor pagination)"
            >
              {loadingOlder ? "Loading…" : "Load older ▾"}
            </button>
          )}
        </div>
      )}
    </Panel>
  );
}

/** A collapsed activity group: renders the representative row + a ×N badge when
 * >1, expandable to show every row in the run. */
function ActivityGroupRow({
  group,
  expanded,
  onToggle,
}: {
  group: CollapsedActivity;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (group.count === 1) return <ActivityRow a={group.head} />;
  return (
    <>
      <div className="activity-row" style={{ cursor: "pointer" }} onClick={onToggle} title="Click to expand repeats">
        <span className="ts">{fmtClock(group.head.ts)}</span>
        <span className={`slot ${group.head.slot_id ? "has" : ""}`}>
          {group.head.slot_id ? `#${group.head.slot_id}` : "—"}
        </span>
        <span className="msg">
          <span className={`type ${activityTypeClass(group.head.event_type)}`}>
            {activityLabel(group.head.event_type)}
          </span>
          <span
            className="mono"
            style={{
              fontSize: 10,
              padding: "0 5px",
              marginRight: 6,
              borderRadius: 8,
              background: "var(--bg-3)",
              color: "var(--fg-1)",
            }}
            title={`${group.count} occurrences collapsed`}
          >
            ×{group.count}
          </span>
          {activitySummary(group.head)}
          <span style={{ color: "var(--fg-3)", marginLeft: 6 }}>{expanded ? "▴" : "▾"}</span>
        </span>
      </div>
      {expanded && group.rows.map((r, i) => <ActivityRow key={r.id ?? i} a={r} indent />)}
    </>
  );
}

// ── activity row rendering helpers (exported pure for vitest) ──────────────

export function activityTypeClass(eventType: string): string {
  if (isFailureEvent(eventType)) return "crit";
  return (
    (
      {
        slot_blocked: "warn",
        slot_stale: "crit",
        slot_compacted: "warn",
        slot_done: "ok",
        blocked_answered: "ok",
        task_dispatched: "cool",
        prerequisite_set: "cool",
        slot_boot: "",
        slot_paused: "warn",
        slot_progress: "",
        session_checkpoint: "cool",
        message_sent: "cool",
      } as Record<string, string>
    )[eventType] || ""
  );
}

export function activityLabel(eventType: string): string {
  return (
    (
      {
        slot_blocked: "blocked",
        slot_stale: "stale",
        slot_compacted: "compacted",
        slot_done: "done",
        blocked_answered: "answered",
        task_dispatched: "dispatched",
        prerequisite_set: "prerequisite",
        slot_boot: "booted",
        slot_paused: "paused",
        slot_resumed: "resumed",
        slot_progress: "progress",
        session_checkpoint: "snapshot",
        message_sent: "msg",
        escalation_dispatch_failed: "escalation failed",
        autospawn_failed: "autospawn failed",
      } as Record<string, string>
    )[eventType] || eventType
  );
}

export function activitySummary(a: ActivityEntry): string {
  const d = a.details || {};
  switch (a.event_type) {
    case "slot_blocked":
      return typeof d.question === "string" ? d.question : "ambiguity escalated";
    case "slot_stale":
      return `silent ${Math.floor(((d.silence_seconds as number) || 0) / 60)}m (threshold ${Math.floor(((d.threshold_seconds as number) || 0) / 60)}m)`;
    case "slot_compacted":
      return `compactions in last hr: ${d.compactions_last_hour ?? "?"}`;
    case "slot_done":
      return `${a.task_id} → ${typeof d.sha === "string" ? d.sha.slice(0, 7) : "?"}${d.evidence ? " · " + String(d.evidence).slice(0, 80) : ""}`;
    case "blocked_answered":
      return `"${d.answer}" — ${d.from}`;
    case "task_dispatched":
      return `${a.task_id} · ${d.reason ?? ""}`;
    case "prerequisite_set":
      return `${d.name} = ${String(d.value)} (${d.set_by})`;
    case "slot_boot":
      return `model=${d.model} ctx=${d.context_pct}%`;
    case "slot_progress":
      return typeof d.message === "string" && d.message ? d.message : "ping";
    case "message_sent":
      return `${d.from || "operator"} → #${a.slot_id} · ${d.text}`;
    case "session_checkpoint":
      return `reason=${d.reason}`;
    default:
      return "";
  }
}

function ActivityRow({ a, indent = false }: { a: ActivityEntry; indent?: boolean }) {
  const [showError, setShowError] = useState(false);
  const failureError = isFailureEvent(a.event_type) ? extractFailureError(a.details) : null;
  const summary = activitySummary(a);

  return (
    <div className="activity-row" style={indent ? { paddingLeft: 18, opacity: 0.85 } : undefined}>
      <span className="ts">{fmtClock(a.ts)}</span>
      <span className={`slot ${a.slot_id ? "has" : ""}`}>{a.slot_id ? `#${a.slot_id}` : "—"}</span>
      <span className="msg">
        <span className={`type ${activityTypeClass(a.event_type)}`}>{activityLabel(a.event_type)}</span>
        {summary}
        {failureError && (
          <>
            {" "}
            <button
              className="mono"
              style={{
                fontSize: 10,
                padding: "0 5px",
                borderRadius: 3,
                background: "var(--status-stale, #b91c1c)",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
              onClick={() => setShowError((v) => !v)}
              title="Show the failure reason (details_json.error)"
            >
              {showError ? "hide reason ▴" : "why? ▾"}
            </button>
            {showError && (
              <span
                className="mono"
                style={{
                  display: "block",
                  marginTop: 3,
                  fontSize: 11,
                  color: "var(--status-stale, #b91c1c)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {failureError}
              </span>
            )}
          </>
        )}
      </span>
    </div>
  );
}

// ─── backlog summary ─────────────────────────────────────────────────────

export function BacklogSummary({
  backlog,
  onOpenDetail,
  onReloadYaml,
  reloading,
}: {
  backlog: StateResponse["backlog_summary"];
  onOpenDetail?: () => void;
  onReloadYaml?: () => void;
  reloading?: boolean;
}) {
  const total = backlog.queued + backlog.dispatched + backlog.done + backlog.cancelled || 1;
  const pct = (n: number) => (n / total) * 100;
  return (
    <Panel
      title="Backlog"
      count={`${total} tasks`}
      tone="backlog"
      right={
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {onReloadYaml && (
            <button
              className="btn btn-xs"
              onClick={onReloadYaml}
              disabled={reloading}
              title="Re-read backlog.yaml from disk on the server — use after editing the file"
            >
              <Icon.Bolt /> {reloading ? "Reloading…" : "Reload YAML"}
            </button>
          )}
          {onOpenDetail && (
            <button
              className="btn btn-xs"
              onClick={onOpenDetail}
              title="See every task with its status, brief and blockers"
            >
              <Icon.Eye /> Detail
            </button>
          )}
        </div>
      }
    >
      <div className="backlog">
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--fg-1)" }}>
            <b style={{ color: "var(--fg-0)" }}>{backlog.done}</b> of {total} done
            <span style={{ color: "var(--fg-3)" }}> · {Math.round((backlog.done / total) * 100)}%</span>
          </div>
          <div
            className="stacked"
            title={`done ${backlog.done} · dispatched ${backlog.dispatched} · queued ${backlog.queued} · cancelled ${backlog.cancelled}`}
          >
            <span className="done" style={{ width: pct(backlog.done) + "%" }} />
            <span className="dispatched" style={{ width: pct(backlog.dispatched) + "%" }} />
            <span className="queued" style={{ width: pct(backlog.queued) + "%" }} />
            <span className="cancelled" style={{ width: pct(backlog.cancelled) + "%" }} />
          </div>
        </div>
        <div className="legend">
          <div className="lg">
            <span className="sw" style={{ background: "var(--status-working)" }} />
            Done <span className="num">{backlog.done}</span>
          </div>
          <div className="lg">
            <span className="sw" style={{ background: "var(--accent)" }} />
            Dispatched <span className="num">{backlog.dispatched}</span>
          </div>
          <div className="lg">
            <span className="sw" style={{ background: "var(--fg-3)" }} />
            Queued <span className="num">{backlog.queued}</span>
          </div>
          <div className="lg">
            <span className="sw" style={{ background: "var(--status-stale)" }} />
            Cancelled <span className="num">{backlog.cancelled}</span>
          </div>
        </div>
      </div>
    </Panel>
  );
}

// ─── accounts panel ──────────────────────────────────────────────────────

interface AccountsPanelProps {
  accounts: AccountView[];
  onClearRateLimit: (accountId: string) => void;
  onReportUsage: (accountId: string) => void;
  onRefreshUsage: (accountId: string) => void;
  refreshingId: string | null;
}

export function AccountsPanel({
  accounts,
  onClearRateLimit,
  onReportUsage,
  onRefreshUsage,
  refreshingId,
}: AccountsPanelProps) {
  if (accounts.length === 0) {
    return (
      <Panel title="Accounts" flush tone="accounts">
        <Empty
          title="No accounts configured"
          sub={
            <>
              Add Claude subscriptions to <span className="mono">accounts.json</span> and restart.
            </>
          }
        />
      </Panel>
    );
  }
  const available = accounts.filter((a) => a.status === "healthy").length;
  return (
    <Panel title="Accounts" count={`${available}/${accounts.length} available`} flush tone="accounts">
      <div className="accounts-list">
        {accounts.map((a) => (
          <AccountRow
            key={a.account_id}
            account={a}
            refreshing={refreshingId === a.account_id}
            onClearRateLimit={() => onClearRateLimit(a.account_id)}
            onReportUsage={() => onReportUsage(a.account_id)}
            onRefreshUsage={() => onRefreshUsage(a.account_id)}
          />
        ))}
      </div>
    </Panel>
  );
}

/** Compact plain-text line of the extra unified rate-limit header telemetry
 *  (status / binding window / credits). Not a chart — just the values we already
 *  receive from the Anthropic `anthropic-ratelimit-unified-*` headers. Renders
 *  nothing until an API probe has populated them. */
function AccountRateLimitDetail({ account: a }: { account: AccountView }) {
  const bits: { text: string; warn: boolean; title?: string }[] = [];
  if (a.unified_status && a.unified_status !== "allowed") {
    bits.push({ text: `status: ${a.unified_status}`, warn: true });
  }
  if (a.representative_claim) {
    // Humanize "five_hour" → "5-hour", "seven_day" → "weekly".
    const claim =
      a.representative_claim === "five_hour"
        ? "5-hour"
        : a.representative_claim === "seven_day"
          ? "weekly"
          : a.representative_claim.replace(/_/g, " ");
    bits.push({ text: `binding: ${claim}`, warn: false });
  }
  if (a.overage_status === "rejected") {
    // "Overage" / "extra usage" = optional pay-as-you-go usage BEYOND the plan's rate
    // limits — wholly separate from the flat Max subscription (which is unaffected).
    // Disabled overage is the DEFAULT for an account that hasn't funded extra usage, so
    // it is NOT an alarm on its own — it only bites once a window hits 100% (then the
    // account hard-stops instead of spilling into paid usage). Render it neutral +
    // informative; escalate to a warning ONLY when a window is actually near its limit,
    // i.e. when that hard-stop is genuinely imminent. (The earlier red `overage: out of
    // credits` chip read like "account is broke" on healthy low-usage accounts.)
    const reasonRaw = a.overage_disabled_reason ? a.overage_disabled_reason.replace(/_/g, " ") : "off";
    const friendly =
      a.overage_disabled_reason === "out_of_credits"
        ? "no overage credits"
        : a.overage_disabled_reason === "org_level_disabled"
          ? "org policy"
          : reasonRaw;
    const nearLimit = a.status === "rate_limited" || Math.max(a.five_hour_pct ?? 0, a.weekly_pct ?? 0) >= 80;
    bits.push({
      text: `extra-usage: off (${friendly})`,
      warn: nearLimit,
      title:
        `Pay-as-you-go usage beyond this plan's rate limits is off (${reasonRaw}). ` +
        "Separate from the flat Max subscription, which is unaffected. No effect until a " +
        "window hits 100% — then this account hard-stops instead of billing extra usage.",
    });
  }
  if (bits.length === 0) return null;
  return (
    <div className="acc-rl-detail mono" title="Anthropic unified rate-limit headers">
      {bits.map((b, i) => (
        <span key={i} className={b.warn ? "acc-rl-warn" : "acc-rl-ok"} title={b.title}>
          {b.text}
        </span>
      ))}
    </div>
  );
}

function SetupTokenBadge({ account: a }: { account: AccountView }) {
  // Phase 4c: show setup-token (1-year long-lived) expiry for r3 accounts.
  // Only renders when alert level is warn/crit/expired; silent when ok or unknown.
  const alert = a.setup_token_alert;
  if (!alert || alert === "ok") return null;
  const days = a.setup_token_expires_in_days;
  const label = alert === "expired" ? "Setup token expired" : `Setup token expires in ${days}d`;
  const btnClass = alert === "expired" || alert === "crit" ? "btn-danger" : "btn-warn";
  const title =
    alert === "expired"
      ? "Long-lived setup-token has expired. Agents on this account will 401. Run `claude setup-token` on a browser machine, update ~/.claude-accounts/<id>.env and accounts.json."
      : `Setup token expires ${a.setup_token_expires_at}. Renew: run \`claude setup-token\` 30 days before expiry and update the env file.`;
  return (
    <span
      className={`btn btn-xs ${btnClass}`}
      style={{ marginLeft: 8, fontSize: 10.5, cursor: "default" }}
      title={title}
    >
      {label}
    </span>
  );
}

function AccountRow({
  account: a,
  refreshing,
  onClearRateLimit,
  onReportUsage,
  onRefreshUsage,
}: {
  account: AccountView;
  refreshing: boolean;
  onClearRateLimit: () => void;
  onReportUsage: () => void;
  onRefreshUsage: () => void;
}) {
  // Prefer the authoritative pct from `claude /usage` when present;
  // fall back to msgs/limit ratio so manual-entry accounts still render.
  const weeklyPct = a.weekly_pct ?? (a.weekly_msg_limit ? (a.weekly_msgs_used / a.weekly_msg_limit) * 100 : 0);
  const fiveHourPct =
    a.five_hour_pct ??
    (a.five_hour_msgs_used && a.weekly_msg_limit
      ? (a.five_hour_msgs_used / Math.max(1, Math.round(a.weekly_msg_limit / 14))) * 100
      : 0);
  const sonnetPct =
    a.weekly_sonnet_pct ?? (a.weekly_msg_limit ? (a.weekly_sonnet_msgs_used / a.weekly_msg_limit) * 100 : 0);
  // The Sonnet bar shares the weekly window — same reset time.
  return (
    <div className={`acc-row status-${a.status}`}>
      <div className="acc-head">
        <span className={`acc-dot ${statusToClass(a.status)}`} />
        <span className="acc-label">{a.label}</span>
        <span className="acc-tier mono">{a.tier}</span>
        <SetupTokenBadge account={a} />
        <span className="spacer" />
        {a.used_by_slots.length > 0 && (
          <span className="acc-used-by mono" title="slots currently using this account">
            in use by {a.used_by_slots.map((s) => `#${s}`).join(", ")}
          </span>
        )}
      </div>
      <div className="acc-bars">
        <BarRow
          label="Weekly"
          pct={weeklyPct}
          resetsAt={a.weekly_resets_at}
          status={a.status}
          title={`${a.weekly_msgs_used} of ${a.weekly_msg_limit} (weekly quota, all models)`}
        />
        {/* Sonnet weekly is only shown when `claude /usage` actually emits the
            "Current week (Sonnet)" bar. The unified rate-limit headers never carry
            it (API path → weekly_sonnet_pct is always null) and the headless /usage
            TUI freezes at 2 bars so the Sonnet bar never renders (verified
            2026-05-25, see usage_tracker.py). Rendering it unconditionally produced
            a permanent, meaningless 0% bar. Gate on a real reading instead — the bar
            auto-reappears if a future claude build ever surfaces the value. */}
        {a.weekly_sonnet_pct != null && (
          <BarRow
            label="Sonnet (wk)"
            pct={sonnetPct}
            resetsAt={a.weekly_resets_at}
            status={a.status}
            title={`Subset of weekly attributed to Sonnet — ${a.weekly_sonnet_msgs_used} msgs`}
            subdued
          />
        )}
        <BarRow
          label="5-hour"
          pct={fiveHourPct}
          resetsAt={a.five_hour_resets_at}
          status={a.status}
          title={`${a.five_hour_msgs_used} messages in the current 5-hour window`}
        />
      </div>
      <AccountRateLimitDetail account={a} />
      <div className="acc-foot">
        {a.status === "rate_limited" ? (
          <>
            <span className="acc-rate-limit">
              ⏱ rate-limited until {a.rate_limited_until ? fmtIn(a.rate_limited_until) : "—"}
            </span>
            <span className="spacer" />
            <button
              className="btn btn-xs"
              onClick={onRefreshUsage}
              disabled={refreshing}
              title="Re-run /usage via the backend's claude binary; may detect that the window has reset"
            >
              <Icon.Refresh /> {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button className="btn btn-xs" onClick={onClearRateLimit} title="Clear rate-limit flag manually">
              <Icon.Check /> Clear
            </button>
          </>
        ) : (
          <>
            <span className="acc-last-used">
              {a.last_used_at ? `last refreshed ${fmtAgo(a.last_used_at)}` : "no usage data yet"}
            </span>
            <span className="spacer" />
            <button
              className="btn btn-xs"
              onClick={onRefreshUsage}
              disabled={refreshing}
              title="Drive `claude /usage` on the backend's box, parse, update this card (~12s)"
            >
              <Icon.Refresh /> {refreshing ? "Refreshing…" : "Refresh from /usage"}
            </button>
            <button
              className="btn btn-xs"
              onClick={onReportUsage}
              title="Enter numbers manually (fallback if /usage parsing breaks)"
            >
              Manual…
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function statusToClass(s: AccountStatus): string {
  return s === "healthy" ? "working" : s === "high_usage" ? "blocked" : "stale";
}

function pctToClass(pct: number, status: AccountStatus): string {
  if (status === "rate_limited") return "stale";
  if (pct >= 80) return "stale";
  if (pct >= 60) return "blocked";
  return "working";
}

function ModelBadge({
  model,
  effort,
  thinking,
}: {
  model: string | null;
  effort: string | null;
  /** Stored as a free-form string for forward-compat (worker can pass
   *  "on"/"off"/null or richer labels). Treated as boolean here:
   *  truthy non-empty value (other than literal "off"/"no"/"false") →
   *  thinking ON → filled brain. Null/empty/"off" → outline. */
  thinking: string | null;
}) {
  const thinkingOn = !!thinking && !/^\s*(off|no|false|0)\s*$/i.test(thinking);
  return (
    <span className="model-cell">
      <span className="badge">{model || "—"}</span>
      {effort && (
        <span className="model-aux mono" title={`Effort: ${effort}`}>
          {effort}
        </span>
      )}
      <span
        className={`model-thinking ${thinkingOn ? "on" : "off"}`}
        title={thinkingOn ? `Thinking on${thinking && thinking !== "on" ? ` (${thinking})` : ""}` : "Thinking off"}
        aria-label={thinkingOn ? "thinking on" : "thinking off"}
      >
        <Icon.Brain filled={thinkingOn} />
      </span>
    </span>
  );
}

function BarRow({
  label,
  pct,
  resetsAt,
  status,
  title,
  subdued,
}: {
  label: string;
  pct: number;
  resetsAt: string | null;
  status: AccountStatus;
  title: string;
  subdued?: boolean;
}) {
  const rounded = Math.round(pct);
  return (
    <div className="acc-bar-row" style={subdued ? { opacity: 0.7 } : undefined}>
      <span className="acc-bar-label">{label}</span>
      <div className="acc-bar" title={title}>
        <div className={`acc-bar-fill ${pctToClass(pct, status)}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="acc-bar-value mono">{rounded}%</span>
      <span className="acc-bar-resets" title={resetsAt ? new Date(resetsAt).toString() : ""}>
        {formatResetsIn(resetsAt)}
      </span>
    </div>
  );
}

function formatResetsIn(isoUtc: string | null): string {
  if (!isoUtc) return "—";
  const target = Date.parse(isoUtc);
  if (!Number.isFinite(target)) return "—";
  const diffSec = Math.round((target - Date.now()) / 1000);
  if (diffSec <= 0) return "now";
  if (diffSec < 60) return `${diffSec}s`;
  const days = Math.floor(diffSec / 86400);
  const hours = Math.floor((diffSec % 86400) / 3600);
  const mins = Math.floor((diffSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

// ─── agents panel (chat with main / review / plan_reconciler, by ROLE) ───
// Role-split (ao_dashboard_fleet_ui_2026_06_26): main, review, and plan_reconciler
// each get their own chat tab; the collapsed `custom` catch-all tab is retired —
// other kind-agents (cicd, plan_health, data_pipeline_failure, workers) surface in
// the Fleet / agent-types panel instead, not as a chat tab.
const ROLES_ORDER: AgentRole[] = ["main", "review", "plan_reconciler"];

interface AgentsPanelProps {
  agents: AgentView[];
  activeRole: AgentRole | null;
  setActiveRole: (r: AgentRole | null) => void;
  history: ChatMessage[];
  historyLoading: boolean;
  onSendToRole: (role: string, text: string) => Promise<void>;
  onPromote: (agentId: string, targetRole: AgentRole) => Promise<void>;
  onArchive: (agentId: string) => Promise<void>;
  onRestore: (agentId: string) => Promise<void>;
  onDelete: (agent: AgentView) => void;
  onEdit: (agent: AgentView) => void;
  onLog: (agent: AgentView) => void;
  onSpawnAgent: () => void;
  showArchived: boolean;
  setShowArchived: (v: boolean) => void;
}

export function AgentsPanel({
  agents,
  activeRole,
  setActiveRole,
  history,
  historyLoading,
  onSendToRole,
  onPromote,
  onArchive,
  onRestore,
  onDelete,
  onEdit,
  onLog,
  onSpawnAgent,
  showArchived,
  setShowArchived,
}: AgentsPanelProps) {
  const visible = showArchived ? agents : agents.filter((a) => a.status !== "archived");
  const rolesPresent = ROLES_ORDER.filter((r) => visible.some((a) => a.role === r));

  // Auto-select first role with an agent.
  React.useEffect(() => {
    if (activeRole === null && rolesPresent.length > 0) {
      setActiveRole(rolesPresent[0]!);
    } else if (activeRole && !rolesPresent.includes(activeRole) && rolesPresent.length > 0) {
      setActiveRole(rolesPresent[0]!);
    }
  }, [activeRole, rolesPresent, setActiveRole]);

  if (agents.length === 0) {
    return (
      <Panel
        title="Agents"
        flush
        tone="agents"
        right={
          <button className="btn btn-sm" onClick={onSpawnAgent}>
            <Icon.Bolt /> Spawn agent
          </button>
        }
      >
        <Empty
          title="No agents connected"
          sub={
            <>
              Click <b>Spawn agent</b> for a boot prompt; paste it into a fresh Claude Code session (after running{" "}
              <span className="mono">/remote-control</span> in that session) and the agent will register itself.
            </>
          }
        />
      </Panel>
    );
  }

  const onlineCount = visible.filter((a) => a.online).length;

  return (
    <Panel
      title="Agents"
      count={`${onlineCount}/${visible.length} online`}
      flush
      tone="agents"
      right={
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            className="btn btn-xs btn-ghost"
            onClick={() => setShowArchived(!showArchived)}
            title="Toggle showing archived agents"
          >
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
          <button className="btn btn-sm" onClick={onSpawnAgent}>
            <Icon.Bolt /> Spawn agent
          </button>
        </div>
      }
    >
      <div className="agents-tabs">
        {rolesPresent.map((r) => {
          const holders = visible.filter((a) => a.role === r);
          const anyOnline = holders.some((a) => a.online);
          return (
            <button
              key={r}
              className={`agents-tab ${r === activeRole ? "active" : ""}`}
              onClick={() => setActiveRole(r)}
            >
              <span className={`status-dot ${anyOnline ? "working" : "stale"}`} />
              {r === "plan_reconciler" ? "plan-reconciler" : r}{" "}
              <span style={{ color: "var(--fg-3)" }}>· {holders.length}</span>
            </button>
          );
        })}
      </div>

      {activeRole && (
        <RoleChat
          role={activeRole}
          holders={visible.filter((a) => a.role === activeRole)}
          history={history}
          loading={historyLoading}
          onSend={(text) => onSendToRole(activeRole, text)}
          onPromote={onPromote}
          onArchive={onArchive}
          onRestore={onRestore}
          onDelete={onDelete}
          onEdit={onEdit}
          onLog={onLog}
        />
      )}
    </Panel>
  );
}

// Message-delivery visibility chip (Plan B, audit Ask 4 — frontend-only, NO
// messaging-layer rewrite). Surfaces the already-served `pending_count` so the
// operator can SEE whether a sent message landed: "queued N" while undelivered,
// "delivered" once the agent has polled and the outbox is empty.
export interface DeliveryChip {
  label: string;
  tone: "queued" | "delivered";
  title: string;
}

export function deliveryChip(pendingCount: number, hasHolder: boolean): DeliveryChip | null {
  if (!hasHolder) return null;
  if (pendingCount > 0) {
    return {
      label: `queued ${pendingCount}`,
      tone: "queued",
      title: `${pendingCount} message${pendingCount === 1 ? "" : "s"} queued — waiting for the agent to /poll`,
    };
  }
  return { label: "delivered", tone: "delivered", title: "Outbox empty — the agent has received all messages" };
}

interface RoleChatProps {
  role: AgentRole;
  holders: AgentView[];
  history: ChatMessage[];
  loading: boolean;
  onSend: (text: string) => Promise<void>;
  onPromote: (agentId: string, targetRole: AgentRole) => Promise<void>;
  onArchive: (agentId: string) => Promise<void>;
  onRestore: (agentId: string) => Promise<void>;
  onDelete: (agent: AgentView) => void;
  onEdit: (agent: AgentView) => void;
  onLog: (agent: AgentView) => void;
}

function RoleChat({
  role,
  holders,
  history,
  loading,
  onSend,
  onPromote,
  onArchive,
  onRestore,
  onDelete,
  onEdit,
  onLog,
}: RoleChatProps) {
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history.length]);

  const currentHolder = holders.find((a) => a.status === "active") ?? holders[0];

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setText("");
    } finally {
      setSending(false);
    }
  };

  const pendingCount = currentHolder?.pending_count ?? 0;

  return (
    <div className="agent-chat">
      <div className="agent-chat-header">
        <span className="agent-chat-identity">
          <span className={`status-dot ${currentHolder?.online ? "working" : "stale"}`} />
          <b style={{ color: "var(--fg-0)" }}>{role}</b>
          {currentHolder && (
            <>
              <span
                className="mono"
                style={{ color: "var(--fg-2)" }}
                title={
                  currentHolder.claude_session_id
                    ? `Claude session: ${currentHolder.claude_session_id}`
                    : currentHolder.agent_id
                }
              >
                {currentHolder.agent_id}
              </span>
              {currentHolder.claude_session_id && (
                <span
                  className="mono"
                  style={{ color: "var(--fg-3)", fontSize: 10 }}
                  title={`claude --resume ${currentHolder.claude_session_id}`}
                >
                  {currentHolder.claude_session_id.slice(0, 8)}…
                </span>
              )}
              {currentHolder.lifecycle && currentHolder.lifecycle !== "persistent" && (
                <span
                  className="status-pill"
                  style={{ background: "var(--bg-3)", color: "var(--fg-3)", fontSize: 10 }}
                  title={
                    currentHolder.lifecycle === "one_shot"
                      ? "lifecycle: one_shot — runs one task then its session exits (expected)"
                      : "lifecycle: scheduled — cron-driven; its session ending is expected"
                  }
                >
                  {currentHolder.lifecycle === "one_shot" ? "1-shot" : "scheduled"}
                </span>
              )}
              <span className={`status-pill status-${currentHolder.status}`}>{currentHolder.status}</span>
              {currentHolder.last_ping && (
                <span style={{ color: "var(--fg-3)", fontSize: 11 }} title={`last ping ${currentHolder.last_ping}`}>
                  {fmtAgo(currentHolder.last_ping)}
                </span>
              )}
              {(() => {
                const chip = deliveryChip(pendingCount, !!currentHolder);
                if (!chip) return null;
                return (
                  <span
                    className="pending-badge"
                    title={chip.title}
                    style={{
                      background:
                        chip.tone === "queued" ? "var(--status-blocked, #b45309)" : "var(--status-working, #15803d)",
                      color: "#fff",
                      padding: "1px 6px",
                      borderRadius: 8,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {chip.label}
                  </span>
                );
              })()}
            </>
          )}
        </span>
        {currentHolder && (
          <span className="agent-chat-actions">
            {currentHolder.rc_url && (
              <a
                href={currentHolder.rc_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-xs"
                title="Open this agent's session in claude.ai/code"
              >
                ↗ claude.ai/code
              </a>
            )}
            <button
              className="btn btn-xs"
              onClick={() => onEdit(currentHolder)}
              title="Edit label / RC URL / machine / model"
            >
              Edit
            </button>
            <button
              className="btn btn-xs"
              onClick={() => onLog(currentHolder)}
              title="Tail this agent's tmux capture-pane (auto-refreshes every 5s)"
            >
              Log
            </button>
            <PromoteMenu agent={currentHolder} currentRole={role} onPromote={onPromote} />
            {currentHolder.status === "archived" ? (
              <button className="btn btn-xs" onClick={() => onRestore(currentHolder.agent_id)}>
                Restore
              </button>
            ) : (
              <button className="btn btn-xs" onClick={() => onArchive(currentHolder.agent_id)}>
                Archive
              </button>
            )}
            <button className="btn btn-xs btn-danger" onClick={() => onDelete(currentHolder)}>
              Delete
            </button>
          </span>
        )}
      </div>

      <div className="agent-chat-scroll" ref={scrollRef}>
        {loading && history.length === 0 ? (
          <div className="agent-chat-empty">loading…</div>
        ) : history.length === 0 ? (
          <div className="agent-chat-empty">
            No conversation yet for role <b>{role}</b>. Type below to start.
          </div>
        ) : (
          history.map((m) => <ChatBubble key={m.id} msg={m} />)
        )}
      </div>

      {!currentHolder?.online && (
        <div className="agent-offline-notice">
          {currentHolder
            ? `${currentHolder.label} is offline. Message will queue until it polls again.`
            : `No active ${role} agent. Message will queue for the next one.`}
        </div>
      )}

      <div className="agent-chat-compose">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Message ${role}…`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
          rows={2}
          disabled={sending}
        />
        <button
          className="btn btn-primary btn-sm"
          disabled={sending || !text.trim()}
          onClick={() => void submit()}
          title="Cmd/Ctrl + Enter to send"
        >
          <Icon.Send /> {sending ? "…" : "Send"}
        </button>
      </div>
    </div>
  );
}

// Short display labels per agent_kind (orchestrator_agent_type_oversight_coverage_
// 2026-06-17). Task-agents register under role="custom", so the kind chip is what
// distinguishes a cicd worker from a plan-reconciler in the custom tab.
const AGENT_KIND_LABEL: Record<string, string> = {
  orchestrator: "orchestrator",
  worker: "worker",
  review: "review",
  cicd: "cicd",
  conflict_resolver: "conflict-resolver",
  plan_health: "plan-health",
  plan_reconciler: "plan-reconciler",
  monitor: "monitor",
  custom: "custom",
};

// ─── agent types panel (by agent_kind — running + past) ───────────────────
//
// Plan B (audit Ask 1): the role chat (main/review/plan_reconciler) collapses
// persistent agents. This panel is the cross-cut by what the agent DOES —
// cicd / conflict-resolver / plan-health / plan-reconciler / monitor are each
// visible here, current AND past (show-finished toggle pulls retained rows).

// Display order for the kind sections (matches AGENT_KIND_LABEL coverage).
export const KINDS_ORDER: AgentKind[] = [
  "orchestrator",
  "review",
  "cicd",
  "conflict_resolver",
  "plan_health",
  "plan_reconciler",
  "monitor",
  "worker",
  "custom",
];

export interface AgentKindGroup {
  kind: AgentKind;
  label: string;
  agents: AgentView[];
  onlineCount: number;
}

/** Group agents by `agent_kind` in KINDS_ORDER, newest-registered first within a
 * kind, online rows ahead of offline. Rows with a null kind (pre-migration) fold
 * into "custom". Empty kinds are dropped. Pure → vitest. */
export function groupAgentsByKind(agents: AgentView[]): AgentKindGroup[] {
  const byKind = new Map<AgentKind, AgentView[]>();
  for (const a of agents) {
    const kind: AgentKind = a.agent_kind ?? "custom";
    const bucket = byKind.get(kind);
    if (bucket) bucket.push(a);
    else byKind.set(kind, [a]);
  }
  const groups: AgentKindGroup[] = [];
  for (const kind of KINDS_ORDER) {
    const list = byKind.get(kind);
    if (!list || list.length === 0) continue;
    const sorted = [...list].sort((a, b) => {
      if (a.online !== b.online) return a.online ? -1 : 1; // online first
      return b.registered_at.localeCompare(a.registered_at); // newest first
    });
    groups.push({
      kind,
      label: AGENT_KIND_LABEL[kind] ?? kind,
      agents: sorted,
      onlineCount: sorted.filter((a) => a.online).length,
    });
  }
  return groups;
}

interface AgentTypesPanelProps {
  agents: AgentView[];
  showFinished: boolean;
  setShowFinished: (v: boolean) => void;
}

export function AgentTypesPanel({ agents, showFinished, setShowFinished }: AgentTypesPanelProps) {
  // Flat table: Type is a COLUMN, not a section head (operator 2026-06-19 — a
  // tabular view: Type · ID · Account · Tmux · Last heartbeat/message · Status).
  // Rows still cluster by kind (KINDS_ORDER) with online-first + newest-first
  // within a kind, by flattening the tested grouping.
  const rows = groupAgentsByKind(agents).flatMap((g) => g.agents);
  const totalOnline = agents.filter((a) => a.online).length;

  return (
    <Panel
      title="Agent types"
      count={`${totalOnline} online`}
      flush
      tone="agents"
      right={
        <button
          className="btn btn-xs btn-ghost"
          onClick={() => setShowFinished(!showFinished)}
          title="Include finished one-shot / scheduled agents (past runs)"
        >
          {showFinished ? "Hide finished" : "Show finished"}
        </button>
      }
    >
      {rows.length === 0 ? (
        <Empty
          title="No agents by type yet"
          sub={
            showFinished
              ? "No running or finished agents recorded."
              : "Escalate / plan-health / conflict-resolver runs appear here. Toggle “Show finished” for past runs."
          }
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="slot-table agent-types-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>ID</th>
                <th>Source</th>
                <th>Task</th>
                <th>Account</th>
                <th>Tmux</th>
                <th>Last heartbeat / message</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <AgentTypeRow key={a.agent_id} a={a} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/** One agent row in the AgentTypesPanel table. Columns: Type (kind + lifecycle pill) ·
 * ID (agent_id; label as tooltip) · Account (account_id) · Tmux (attach command, click
 * to copy) · Last heartbeat + last message (finished rows show finished_at + exit_reason
 * — audit Ask 1: "past escalate/plan-health runs") · Status. */
function AgentTypeRow({ a }: { a: AgentView }) {
  const finished = a.status === "finished";
  const kindLabel = AGENT_KIND_LABEL[a.agent_kind ?? "custom"] ?? a.agent_kind ?? "custom";
  return (
    <tr className={`status-${a.status}`}>
      {/* Type */}
      <td>
        <span style={{ color: "var(--fg-1)", fontWeight: 600 }}>{kindLabel}</span>
        {a.lifecycle && a.lifecycle !== "persistent" && (
          <span
            className="status-pill"
            style={{ background: "var(--bg-3)", color: "var(--fg-3)", fontSize: 10, marginLeft: 6 }}
            title={`lifecycle: ${a.lifecycle}`}
          >
            {a.lifecycle === "one_shot" ? "1-shot" : "scheduled"}
          </span>
        )}
      </td>
      {/* ID — agent_id (label as tooltip) + claude_session_id for --resume */}
      <td>
        <span className="mono" style={{ color: "var(--fg-1)" }} title={a.label}>
          {a.agent_id}
        </span>
        {a.claude_session_id && (
          <div
            className="mono"
            style={{ color: "var(--fg-3)", fontSize: 10, marginTop: 2 }}
            title={`Claude session: ${a.claude_session_id}`}
          >
            {a.claude_session_id.slice(0, 8)}…
          </div>
        )}
      </td>
      {/* Source — kind-specific context (repo, alert-id, etc.) */}
      <td>
        {a.source ? (
          <span className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>
            {a.source}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}
      </td>
      {/* Task — what the agent is currently doing */}
      <td>
        {a.current_task ? (
          <span className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>
            {a.current_task}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}
      </td>
      {/* Account */}
      <td>
        {a.account_id ? (
          <span className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>
            {a.account_id}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}
      </td>
      {/* Tmux attach command — click to copy */}
      <td>
        {a.tmux_session ? (
          <span
            className="mono"
            style={{
              fontSize: 11,
              padding: "1px 5px",
              borderRadius: 3,
              background: "var(--bg-3)",
              color: "var(--fg-2)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            title={`Click to copy. Attach: tmux attach -t ${a.tmux_session}`}
            onClick={() => {
              void navigator.clipboard?.writeText(`tmux attach -t ${a.tmux_session}`);
            }}
          >
            tmux attach -t {a.tmux_session}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}
      </td>
      {/* Last heartbeat / message */}
      <td>
        <div style={{ color: "var(--fg-3)", fontSize: 11 }}>
          {finished
            ? a.finished_at
              ? `finished ${fmtAgo(a.finished_at)}`
              : "finished"
            : a.last_ping
              ? `${a.online ? "active" : "last seen"} ${fmtAgo(a.last_ping)}`
              : "—"}
        </div>
        {finished && a.exit_reason ? (
          <div className="mono" style={{ color: "var(--fg-2)", fontSize: 11 }} title={a.exit_reason}>
            {a.exit_reason}
          </div>
        ) : (
          a.last_msg && (
            <div
              style={{
                color: "var(--fg-2)",
                fontSize: 12,
                maxWidth: 360,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={a.last_msg}
            >
              {a.last_msg}
            </div>
          )
        )}
      </td>
      {/* Status */}
      <td>
        <span className="status-cell">
          <span className={`status-dot ${a.online ? "working" : finished ? "idle" : "stale"}`} />
          <span className={`status-pill status-${a.status}`}>{a.status}</span>
        </span>
      </td>
    </tr>
  );
}

function PromoteMenu({
  agent,
  currentRole,
  onPromote,
}: {
  agent: AgentView;
  currentRole: AgentRole;
  onPromote: (agentId: string, targetRole: AgentRole) => Promise<void>;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", click);
    return () => window.removeEventListener("mousedown", click);
  }, [open]);
  const targets = ROLES_ORDER.filter((r) => r !== currentRole);
  return (
    <div className="anchor" ref={ref}>
      <button className="btn btn-xs" onClick={() => setOpen((o) => !o)}>
        Promote ▾
      </button>
      {open && (
        <div className="menu-pop" style={{ minWidth: 140 }} role="menu">
          {targets.map((r) => (
            <button
              key={r}
              className="m-row"
              style={{
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                padding: "6px 10px",
              }}
              onClick={() => {
                setOpen(false);
                void onPromote(agent.agent_id, r);
              }}
            >
              → {r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChatBubble({ msg }: { msg: ChatMessage }) {
  const isFromAgent = msg.direction === "from_agent";
  const senderShort = msg.from_agent_id ? msg.from_agent_id.split("-").slice(-1)[0].slice(0, 6) : null;
  return (
    <div className={`chat-bubble ${isFromAgent ? "from-agent" : "to-agent"}`}>
      <div className="chat-bubble-head">
        <span className="chat-bubble-role">
          {msg.from_role}
          {senderShort ? ` (${senderShort})` : ""}
        </span>
        <span className="chat-bubble-ts">{fmtAgo(msg.created_at)}</span>
      </div>
      <div className="chat-bubble-body">{msg.content}</div>
    </div>
  );
}
